import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICES } from "@/data/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      return "Please enter a valid email";
    if (!form.phone.trim()) return "Please enter your phone number";
    if (!form.service) return "Please select a service";
    if (!form.message.trim() || form.message.trim().length < 5)
      return "Please share a short message";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm(initialForm);
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        "Something went wrong. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-32 bg-[#F2FAF4] dark:bg-[#0E2A17]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs uppercase tracking-[0.18em] font-semibold text-[#1F8A3A]">
              Get in touch
            </span>
            <h2
              data-testid="contact-heading"
              className="mt-4 font-display text-3xl md:text-5xl font-semibold tracking-tight text-[#0E2A17] dark:text-[#F2FAF4]"
            >
              Book an inspection. Honest answers.
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#3F5549] dark:text-[#B9CDC0] leading-relaxed">
              Tell us what you're seeing — even if you're not sure it's mold.
              We'll get back within one business day with next steps.
            </p>
            <p
              data-testid="contact-waiver-note"
              className="mt-4 rounded-2xl border border-[#1F8A3A]/20 bg-[#E3F5EA] dark:bg-[#12331E] px-5 py-4 text-sm text-[#0E2A17] dark:text-[#F2FAF4] leading-relaxed"
            >
              <span className="font-semibold text-[#1F8A3A]">
                Inspection fee waived
              </span>{" "}
              when mold is confirmed and you book the remediation on the same
              visit — you pay only for the cleaning job.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 rounded-2xl bg-white dark:bg-[#0A2513] border border-[#E3F5EA] dark:border-[#1A3925] p-5 hover:border-[#1F8A3A] transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-[#1F8A3A]/10 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-[#1F8A3A]" />
                </div>
                <div>
                  <div className="text-xs text-[#3F5549] dark:text-[#B9CDC0] uppercase tracking-wider">
                    Call
                  </div>
                  <div className="font-semibold text-[#0E2A17] dark:text-[#F2FAF4]">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl bg-white dark:bg-[#0A2513] border border-[#E3F5EA] dark:border-[#1A3925] p-5 hover:border-[#1F8A3A] transition-colors"
              >
                <div className="h-12 w-12 rounded-xl bg-[#1F8A3A]/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-[#1F8A3A]" />
                </div>
                <div>
                  <div className="text-xs text-[#3F5549] dark:text-[#B9CDC0] uppercase tracking-wider">
                    Email
                  </div>
                  <div className="font-semibold text-[#0E2A17] dark:text-[#F2FAF4] break-all">
                    {COMPANY.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 rounded-2xl bg-white dark:bg-[#0A2513] border border-[#E3F5EA] dark:border-[#1A3925] p-5">
                <div className="h-12 w-12 rounded-xl bg-[#1F8A3A]/10 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-[#1F8A3A]" />
                </div>
                <div>
                  <div className="text-xs text-[#3F5549] dark:text-[#B9CDC0] uppercase tracking-wider">
                    Service Area
                  </div>
                  <div className="font-semibold text-[#0E2A17] dark:text-[#F2FAF4]">
                    {COMPANY.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl bg-white dark:bg-[#0A2513] border border-[#E3F5EA] dark:border-[#1A3925] p-7 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-[#0E2A17] dark:text-[#F2FAF4]">
                    Full name
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Smith"
                    className="mt-2 h-12 rounded-xl border-[#E3F5EA] dark:border-[#1A3925] focus-visible:ring-[#1F8A3A]"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#0E2A17] dark:text-[#F2FAF4]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-2 h-12 rounded-xl border-[#E3F5EA] dark:border-[#1A3925] focus-visible:ring-[#1F8A3A]"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#0E2A17] dark:text-[#F2FAF4]">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(647) 555-0123"
                    className="mt-2 h-12 rounded-xl border-[#E3F5EA] dark:border-[#1A3925] focus-visible:ring-[#1F8A3A]"
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <Label htmlFor="service" className="text-[#0E2A17] dark:text-[#F2FAF4]">
                    Service needed
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => update("service")(v)}
                  >
                    <SelectTrigger
                      id="service"
                      data-testid="contact-select-service"
                      className="mt-2 h-12 rounded-xl border-[#E3F5EA] dark:border-[#1A3925] focus:ring-[#1F8A3A]"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent data-testid="contact-select-content">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.key}
                          value={s.title}
                          data-testid={`contact-select-option-${s.key}`}
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Not sure yet" data-testid="contact-select-option-other">
                        Not sure yet
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-[#0E2A17] dark:text-[#F2FAF4]">
                  Tell us what you're seeing
                </Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Musty smell in the basement, dark spots around the bathroom ceiling..."
                  className="mt-2 rounded-xl border-[#E3F5EA] dark:border-[#1A3925] focus-visible:ring-[#1F8A3A] resize-none"
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-xs text-[#3F5549] dark:text-[#B9CDC0] sm:max-w-sm">
                  We respect your privacy — we'll never share your details.
                  Typical response within one business day.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#29ABE2] px-8 py-4 text-sm font-semibold text-white hover:bg-[#1A8ABF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors min-w-[180px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Request <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
